import { useState } from 'react'
import { router } from '@inertiajs/react'
import { useFlashToast } from '../../../hooks/useFlashToast'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faMagnifyingGlass, faCrown, faEyeSlash,
  faComments, faIdCard, faPaperclip, faBuilding, faGlobe,
  faChevronRight, faFilter, faInbox, faDownload
} from '@fortawesome/free-solid-svg-icons'
import SuperadminSidebar from '../../../components/superadmin/Sidebar'
import SuperadminTopbar from '../../../components/superadmin/Topbar'

const STATUS_STYLE = {
  menunggu: 'bg-blue-900/50 text-blue-300 ring-blue-700',
  diproses: 'bg-amber-900/40 text-amber-300 ring-amber-700',
  selesai:  'bg-emerald-900/40 text-emerald-300 ring-emerald-700',
  ditolak:  'bg-red-900/40 text-red-300 ring-red-700',
}

export default function SuperadminReportsIndex({
  current_user,
  reports = [],
  filters = {},
  stats = {},
  categories = [],
}) {
  useFlashToast()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [searchVal, setSearchVal] = useState(filters.query || '')
  const [showFilters, setShowFilters] = useState(false)
  const [localFilters, setLocalFilters] = useState({
    anonymous: filters.anonymous || '',
    category: filters.category || '',
    user_type: filters.user_type || '',
  })

  const applyFilters = (status = filters.status) => {
    router.get('/superadmin/reports', {
      status,
      q: searchVal,
      anonymous: localFilters.anonymous,
      category: localFilters.category,
      user_type: localFilters.user_type,
    }, { preserveState: true })
  }

  const handleSearch = (e) => {
    e.preventDefault()
    applyFilters()
  }

  const TABS = [
    { key: '', label: 'Semua', value: stats.total },
    { key: 'menunggu', label: 'Menunggu', value: stats.menunggu },
    { key: 'diproses', label: 'Diproses', value: stats.diproses },
    { key: 'selesai', label: 'Selesai', value: stats.selesai },
    { key: 'ditolak', label: 'Ditolak', value: stats.ditolak },
  ]

  return (
    <div className="flex h-screen bg-gray-950 font-sans overflow-hidden text-gray-100">
      <SuperadminSidebar current_user={current_user} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <SuperadminTopbar current_user={current_user} onMenuToggle={() => setSidebarOpen(true)}>
          <form onSubmit={handleSearch} className="flex-1 max-w-md">
            <div className="relative">
              <FontAwesomeIcon icon={faMagnifyingGlass} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                value={searchVal}
                onChange={e => setSearchVal(e.target.value)}
                placeholder="Cari kode, judul laporan..."
                className="w-full pl-9 pr-4 py-2 text-sm bg-gray-800 border border-gray-700 text-gray-200 placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
              />
            </div>
          </form>
        </SuperadminTopbar>

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <FontAwesomeIcon icon={faCrown} className="w-4 h-4 text-indigo-400" />
                <h1 className="text-xl font-bold text-white">Semua Laporan</h1>
              </div>
              <p className="text-sm text-gray-500">Pantau seluruh laporan sistem — tampilan oversight superadmin</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <a
                href={`/superadmin/reports/export?status=${filters.status || ''}&q=${encodeURIComponent(filters.query || '')}&anonymous=${localFilters.anonymous}&category=${localFilters.category}&user_type=${localFilters.user_type}`}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-500"
              >
                <FontAwesomeIcon icon={faDownload} className="w-3.5 h-3.5" />
                Export CSV
              </a>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-gray-900 border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-800"
              >
              <FontAwesomeIcon icon={faFilter} className="w-3.5 h-3.5" />
              Filter Lanjutan
            </button>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {[
              { label: 'Total', value: stats.total, color: 'text-indigo-400' },
              { label: 'Anonim', value: stats.anonymous, color: 'text-violet-400' },
              { label: 'Identitas Pending', value: stats.identity_pending, color: 'text-amber-400' },
              { label: 'Selesai', value: stats.selesai, color: 'text-emerald-400' },
            ].map(s => (
              <div key={s.label} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                <div className={`text-2xl font-bold ${s.color}`}>{s.value ?? 0}</div>
                <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>

          {showFilters && (
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Anonim</label>
                <select
                  value={localFilters.anonymous}
                  onChange={e => setLocalFilters({ ...localFilters, anonymous: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded-lg px-3 py-2"
                >
                  <option value="">Semua</option>
                  <option value="1">Anonim saja</option>
                  <option value="0">Non-anonim</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Kategori</label>
                <select
                  value={localFilters.category}
                  onChange={e => setLocalFilters({ ...localFilters, category: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded-lg px-3 py-2"
                >
                  <option value="">Semua kategori</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Tipe Pelapor</label>
                <select
                  value={localFilters.user_type}
                  onChange={e => setLocalFilters({ ...localFilters, user_type: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded-lg px-3 py-2"
                >
                  <option value="">Semua tipe</option>
                  <option value="internal">Internal</option>
                  <option value="external">External</option>
                </select>
              </div>
              <div className="sm:col-span-3 flex justify-end">
                <button onClick={() => applyFilters()} className="px-4 py-2 text-sm font-semibold bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg">
                  Terapkan Filter
                </button>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-1 mb-4 bg-gray-900 border border-gray-800 rounded-xl p-1 w-fit">
            {TABS.map(t => (
              <button
                key={t.key}
                onClick={() => applyFilters(t.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filters.status === t.key
                    ? 'bg-indigo-600 text-white'
                    : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                }`}
              >
                {t.label}
                <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full ${
                  filters.status === t.key ? 'bg-white/20' : 'bg-gray-800 text-gray-500'
                }`}>{t.value}</span>
              </button>
            ))}
          </div>

          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            {reports.length === 0 ? (
              <div className="p-12 text-center">
                <FontAwesomeIcon icon={faInbox} className="w-8 h-8 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">Tidak ada laporan ditemukan</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-800/60 text-[11px] font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-800">
                      <th className="text-left px-5 py-3">Kode</th>
                      <th className="text-left px-5 py-3">Laporan</th>
                      <th className="text-left px-5 py-3 hidden lg:table-cell">Pelapor</th>
                      <th className="text-left px-5 py-3 hidden md:table-cell">Indikator</th>
                      <th className="text-left px-5 py-3">Status</th>
                      <th className="text-left px-5 py-3 hidden sm:table-cell">Tanggal</th>
                      <th className="px-5 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {reports.map(report => (
                      <tr key={report.id} className="hover:bg-gray-800/40 transition-colors group">
                        <td className="px-5 py-4 font-mono text-xs text-gray-500 whitespace-nowrap">{report.tracking_code}</td>
                        <td className="px-5 py-4 min-w-[200px]">
                          <p className="font-medium text-gray-200 line-clamp-1">{report.title}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{report.category}</p>
                        </td>
                        <td className="px-5 py-4 hidden lg:table-cell">
                          {report.anonymous && !report.identity_reveal_approved ? (
                            <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                              <FontAwesomeIcon icon={faEyeSlash} className="w-3 h-3" /> Anonim
                            </span>
                          ) : (
                            <div className="text-xs">
                              <p className="text-gray-300">{report.reporter?.label}</p>
                              {report.reporter?.type && (
                                <span className="inline-flex items-center gap-1 text-gray-500 mt-0.5">
                                  <FontAwesomeIcon icon={report.reporter.type === 'internal' ? faBuilding : faGlobe} className="w-3 h-3" />
                                  {report.reporter.type}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-5 py-4 hidden md:table-cell">
                          <div className="flex flex-wrap gap-1.5">
                            {report.communication_open && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-sky-900/40 text-sky-300 ring-1 ring-sky-800">
                                <FontAwesomeIcon icon={faComments} className="w-2.5 h-2.5" /> Chat
                              </span>
                            )}
                            {report.identity_reveal_requested && !report.identity_reveal_approved && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-amber-900/40 text-amber-300 ring-1 ring-amber-800">
                                <FontAwesomeIcon icon={faIdCard} className="w-2.5 h-2.5" /> Identitas
                              </span>
                            )}
                            {report.attachments_count > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-violet-900/40 text-violet-300 ring-1 ring-violet-800">
                                <FontAwesomeIcon icon={faPaperclip} className="w-2.5 h-2.5" /> {report.attachments_count}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ring-1 ${STATUS_STYLE[report.status]}`}>
                            {report.status_label}
                          </span>
                        </td>
                        <td className="px-5 py-4 hidden sm:table-cell text-xs text-gray-500 whitespace-nowrap">{report.created_at}</td>
                        <td className="px-5 py-4 text-right">
                          <a
                            href={`/superadmin/reports/${report.id}`}
                            className="inline-flex items-center gap-1 text-xs font-medium text-indigo-400 hover:text-indigo-300 opacity-70 group-hover:opacity-100"
                          >
                            Detail <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" />
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}
